/**
 * MobileOrderFullScreen — tier 2 of the mobile two-tier detail: a full-screen
 * push (slide-in) matching Trioty Orders Mobile. It reuses OrderBody (the real
 * timeline / items / payment / customer / delivery sections) and the shared
 * useOrderActions hook + its dialogs, so every action — quick actions, payment
 * review, decline, cancel, discard, ship chooser, undo, invoice — is preserved.
 */

import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSlideScreen } from "@/shared/hooks/useSlideScreen";
import { LoadingSkeleton, useToast, ConfirmDialog } from "@a2z/ui";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { ErrorState } from "@/shared/components/ErrorState";
import { useLocale, useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { SellerOrderDetail } from "@/lib/api/types";
import { useOrderByIdQuery } from "@/modules/orders/services/useOrderByIdQuery";
import { useOrderInvoiceDownload } from "@/modules/orders/services/useOrderInvoiceDownload";
import { isInvoiceAvailable } from "@/modules/orders/lib/invoice";
import { useThemeStore } from "@/shared/theme/useTheme";
import {
  PullToRefreshProvider,
  PullToRefreshHost,
  usePullToRefresh,
} from "@/shared/pull-to-refresh";
import { MobileOrderFullBody } from "./MobileOrderFullBody";
import { useOrderActions } from "@/modules/orders/components/order-detail-modal/useOrderActions";
import { OrderActionConfirm } from "@/modules/orders/components/OrderActionConfirm";
import { ShipOrderSheet } from "@/modules/orders/components/order-detail-modal/ShipOrderSheet";
import { CancelOrderSheet } from "@/modules/orders/components/order-detail-modal/CancelOrderSheet";
import { DeclineOrderSheet } from "@/modules/orders/components/order-detail-modal/DeclineOrderSheet";
import { OrderUndoControl } from "@/modules/orders/components/order-detail-modal/OrderUndoControl";
import { PaymentReviewDialog } from "@/modules/orders/components/PaymentReviewDialog";

type Props = {
  storeSlug: string;
  orderId: string | null;
  onClose: () => void;
};

export function MobileOrderFullScreen({ storeSlug, orderId, onClose }: Props) {
  // Mount only while open; a fresh mount per order id replays the slide-in.
  if (!orderId) return null;
  return <Screen key={orderId} storeSlug={storeSlug} orderId={orderId} onClose={onClose} />;
}

function Screen({
  storeSlug,
  orderId,
  onClose,
}: {
  storeSlug: string;
  orderId: string;
  onClose: () => void;
}) {
  // Slide in on open; the back button (via `close`) plays the reverse slide-out
  // before calling onClose. Shared with the product detail + edit screens.
  const { ref, close } = useSlideScreen<HTMLDivElement>(onClose);

  // Portal to <body>: rendered inline, this `fixed` overlay would sit inside the
  // AppShell <main> (an overflow-y-auto scroll container), where iOS Safari
  // couples its scroll to the parent and overscroll chains into the page behind.
  return createPortal(
    <div
      ref={ref}
      className="fixed inset-0 z-[70] flex flex-col bg-[color:var(--canvas)] lg:hidden"
    >
      <Panel storeSlug={storeSlug} orderId={orderId} onClose={close} />
    </div>,
    document.body,
  );
}

function Panel({
  storeSlug,
  orderId,
  onClose,
}: {
  storeSlug: string;
  orderId: string;
  onClose: () => void;
}) {
  const t = useTranslations();
  const { data: order, isLoading, error, refetch } = useOrderByIdQuery({ storeSlug, orderId });

  if (error && !isLoading) {
    return (
      <>
        <TopShell onClose={onClose} code={null} right={null} />
        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          <ErrorState
            message={t(keys.orders.fetchTimeoutFallback)}
            onRetry={() => void refetch()}
          />
        </div>
      </>
    );
  }
  if (isLoading || !order) {
    return (
      <>
        <TopShell onClose={onClose} code={null} right={null} />
        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          <LoadingSkeleton variant="card" rows={6} />
        </div>
      </>
    );
  }
  return (
    <LoadedScreen
      order={order}
      storeSlug={storeSlug}
      onClose={onClose}
      refetch={() => refetch()}
    />
  );
}

/** Wraps the loaded detail in a local pull-to-refresh scope (the full-screen
 *  overlay has its own scroll container, separate from the app-level host). */
function LoadedScreen(props: {
  order: SellerOrderDetail;
  storeSlug: string;
  onClose: () => void;
  refetch: () => Promise<unknown>;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  return (
    <PullToRefreshProvider>
      <LoadedScreenInner {...props} scrollRef={scrollRef} />
      <PullToRefreshHost scrollRef={scrollRef} />
    </PullToRefreshProvider>
  );
}

/** Shared top-bar chrome: Back · order code · a right-hand actions cluster. */
function TopShell({
  onClose,
  code,
  right,
}: {
  onClose: () => void;
  code: string | null;
  right: React.ReactNode;
}) {
  const t = useTranslations();
  return (
    <div
      className="flex shrink-0 items-center justify-between gap-2 px-3 pb-3"
      style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 12px)" }}
    >
      <button
        type="button"
        onClick={onClose}
        aria-label={t(keys.common.back)}
        className="flex h-10 w-10 items-center justify-center rounded-full border border-[color:var(--card-br)] bg-[color:var(--soft2)] text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--soft)]"
      >
        <BackIcon />
      </button>
      <div className="min-w-0 flex-1 text-center">
        {code ? (
          <div className="truncate text-[15px] font-bold text-[color:var(--text-primary)]">
            #{code}
          </div>
        ) : null}
      </div>
      <div className="flex items-center gap-1.5">{right}</div>
    </div>
  );
}

/* ── Loaded screen: single useOrderActions powers the header ⋯ menu (secondary
      actions), the bottom bar (invoice · primary · WhatsApp), and every dialog. */
function LoadedScreenInner({
  order,
  storeSlug,
  onClose,
  refetch,
  scrollRef,
}: {
  order: SellerOrderDetail;
  storeSlug: string;
  onClose: () => void;
  refetch: () => Promise<unknown>;
  scrollRef: React.RefObject<HTMLDivElement | null>;
}) {
  const t = useTranslations();
  // Pull down at the top of the order body to refetch it.
  usePullToRefresh(() => refetch());
  const locale = useLocale();
  const isDark = useThemeStore((s) => s.resolvedDark);
  const toast = useToast();
  const [reviewOpen, setReviewOpen] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);
  const canReview = order.status === "pending_payment_verification" && storeSlug.length > 0;
  const { download, isLoading: invoiceLoading } = useOrderInvoiceDownload();
  const {
    primary,
    primaryConfirm,
    secondary,
    dialogState,
    isSaving,
    isDraft,
    isDraftSaving,
    submitDeclineWith,
  } = useOrderActions({
    order,
    storeSlug,
    onReviewPayment: canReview ? () => setReviewOpen(true) : undefined,
    onClose,
  });

  const canCancel = secondary.some((a) => a.key === "cancel");
  const canDecline = secondary.some((a) => a.key === "decline");
  const invoiceAvail = isInvoiceAvailable(order.paymentMethod, order.status);
  const isShipAction = primaryConfirm?.toStatus === "shipped";
  const hasMenu = canDecline || canCancel || isDraft;
  const menuItem =
    "flex w-full items-center rounded-[10px] px-2.5 py-2.5 text-start text-[14px] font-semibold text-[color:var(--error-500)] data-[focus]:bg-[color:var(--soft)]";

  return (
    <>
      <TopShell
        onClose={onClose}
        code={null}
        right={
          <>
            {hasMenu ? (
              <Menu as="div" className="relative flex">
                <MenuButton
                  aria-label={t(keys.orders.mMore)}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-[color:var(--card-br)] bg-[color:var(--soft2)] text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--soft)]"
                >
                  <DotsIcon />
                </MenuButton>
                <MenuItems
                  anchor="bottom end"
                  className="z-[80] mt-1 w-[210px] rounded-[14px] border border-[color:var(--card-br)] bg-[color:var(--card)] p-1.5 shadow-[0_20px_54px_rgba(0,0,0,0.28)] focus:outline-none"
                >
                  {canDecline ? (
                    <MenuItem>
                      <button type="button" onClick={() => setDeclineOpen(true)} className={menuItem}>
                        {t(keys.orders.actionDeclineTitle)}
                      </button>
                    </MenuItem>
                  ) : null}
                  {canCancel ? (
                    <MenuItem>
                      <button
                        type="button"
                        onClick={() => dialogState.setCancelOpen(true)}
                        className={menuItem}
                      >
                        {t(keys.orders.actionCancelTitle)}
                      </button>
                    </MenuItem>
                  ) : null}
                  {isDraft ? (
                    <MenuItem>
                      <button
                        type="button"
                        onClick={() => dialogState.setDiscardOpen(true)}
                        className={menuItem}
                      >
                        {t(keys.orders.discardDraftAction)}
                      </button>
                    </MenuItem>
                  ) : null}
                </MenuItems>
              </Menu>
            ) : null}
          </>
        }
      />

      {/* Body */}
      <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        <MobileOrderFullBody
          order={order}
          locale={locale}
          isDark={isDark}
          storeSlug={storeSlug}
        />
      </div>

      {/* Action dialogs (identical wiring to the drawer footer) */}
      {primaryConfirm && !isShipAction ? (
        <OrderActionConfirm
          isOpen={primaryConfirm.open}
          orderCode={order.orderCode}
          fromStatus={primaryConfirm.fromStatus}
          toStatus={primaryConfirm.toStatus}
          bodyKey={primaryConfirm.needsEta ? null : primaryConfirm.copy.bodyKey}
          confirmKey={primaryConfirm.copy.confirmKey}
          needsEta={primaryConfirm.needsEta}
          isSubmitting={isSaving}
          onConfirm={(eta) => void primaryConfirm.run(eta)}
          onClose={() => primaryConfirm.setOpen(false)}
        />
      ) : null}
      {primaryConfirm && isShipAction ? (
        <ShipOrderSheet
          storeSlug={storeSlug}
          orderId={order.id}
          isOpen={primaryConfirm.open}
          onClose={() => primaryConfirm.setOpen(false)}
          onShipManually={() => primaryConfirm.run(null)}
          isShippingManually={isSaving}
        />
      ) : null}
      <DeclineOrderSheet
        isOpen={declineOpen}
        onClose={() => setDeclineOpen(false)}
        onConfirm={(reasonCode) => {
          void submitDeclineWith(reasonCode);
          setDeclineOpen(false);
        }}
        isSubmitting={isSaving}
      />
      <CancelOrderSheet
        isOpen={dialogState.cancelOpen}
        onClose={() => dialogState.setCancelOpen(false)}
        order={order}
        onConfirm={(reasonCode, note) => void dialogState.onConfirmCancel(reasonCode, note)}
        isSubmitting={isSaving}
      />
      <ConfirmDialog
        isOpen={dialogState.discardOpen}
        title={t(keys.orders.discardDraftConfirmTitle)}
        body={t(keys.orders.discardDraftConfirmBody)}
        confirmLabel={t(keys.orders.discardDraftAction)}
        cancelLabel={t(keys.common.cancel)}
        isSubmitting={isDraftSaving}
        onConfirm={() => void dialogState.onConfirmDiscard()}
        onCancel={() => dialogState.setDiscardOpen(false)}
      />
      {canReview ? (
        <PaymentReviewDialog
          locale={locale}
          storeSlug={storeSlug}
          orderId={order.id}
          isOpen={reviewOpen}
          onClose={() => setReviewOpen(false)}
          onSuccess={() => {
            setReviewOpen(false);
            toast.success(t(keys.orders.statusSavedMicro));
          }}
        />
      ) : null}

      {/* Bottom bar: undo (when applicable) + invoice · primary · WhatsApp */}
      <div
        className="flex shrink-0 flex-col gap-2.5 border-t border-[color:var(--line)] px-4 pt-3"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)" }}
      >
        <OrderUndoControl order={order} storeSlug={storeSlug} />
        <div className="flex items-center gap-2">
          {invoiceAvail ? (
            <button
              type="button"
              onClick={() => void download(order.id)}
              disabled={invoiceLoading}
              aria-label={t(keys.orders.downloadInvoice)}
              title={t(keys.orders.downloadInvoice)}
              className="flex h-[50px] w-[50px] shrink-0 items-center justify-center rounded-[13px] border border-[color:var(--border-strong)] bg-[color:var(--card)] text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--soft)] disabled:opacity-50"
            >
              {invoiceLoading ? (
                <svg aria-hidden="true" className="h-[18px] w-[18px] animate-spin" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="32" strokeDashoffset="12" />
                </svg>
              ) : (
                <PrintIcon />
              )}
            </button>
          ) : null}
          {primary ? (
            <button
              type="button"
              onClick={() => void primary.run()}
              disabled={primary.disabled}
              className="flex h-[50px] flex-1 items-center justify-center gap-2 rounded-[13px] bg-[color:var(--accent-500)] px-4 text-[15px] font-semibold text-[color:var(--on-accent)] transition-opacity hover:opacity-90 disabled:opacity-50"
            >
              <CheckIcon />
              {t(primary.labelKey)}
            </button>
          ) : null}
          <a
            href={`https://wa.me/${order.customer.phoneE164.replace(/[^0-9]/g, "")}`}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="WhatsApp"
            className="flex h-[50px] w-[50px] shrink-0 items-center justify-center rounded-[13px] border border-[color:rgba(74,222,128,0.32)] bg-[color:rgba(74,222,128,0.14)] text-[#22A559]"
          >
            <WhatsAppIcon />
          </a>
        </div>
      </div>
    </>
  );
}

const ICON = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor" as const,
  strokeWidth: 1.9,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
};
function BackIcon() {
  return (
    <svg {...ICON} width={18} height={18} strokeWidth={2} className="rtl:-scale-x-100">
      <path d="m15 18-6-6 6-6" />
    </svg>
  );
}
function DotsIcon() {
  return (
    <svg {...ICON} width={16} height={16} stroke="none" fill="currentColor">
      <circle cx="5" cy="12" r="2" />
      <circle cx="12" cy="12" r="2" />
      <circle cx="19" cy="12" r="2" />
    </svg>
  );
}
function PrintIcon() {
  return (
    <svg {...ICON} width={18} height={18} strokeWidth={1.7}>
      <path d="M6 9V4h12v5" />
      <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
      <rect x="6" y="14" width="12" height="8" rx="1" />
    </svg>
  );
}
function CheckIcon() {
  return (
    <svg {...ICON} width={16} height={16} strokeWidth={2}>
      <path d="M5 12l5 5L20 7" />
    </svg>
  );
}
function WhatsAppIcon() {
  return (
    <svg {...ICON} width={18} height={18}>
      <path d="M21 11.5a8.5 8.5 0 0 1-12.3 7.6L3 21l1.9-5.7A8.5 8.5 0 1 1 21 11.5Z" />
    </svg>
  );
}
